import { OrderSource } from "~/entities/orderEntities";
import { createPath } from "@remix-run/router";
import compact from "lodash/compact";

const formatUrlSegment = (segment: string): string => {
  return segment.toLowerCase().replace(/_/g, "-");
};

enum Page {
  Address,
  Sites,
  Orders,
  Order,
  Checkout,
}

type PageParams = {
  [Page.Address]: null;
  [Page.Sites]: null;
  [Page.Orders]: null;
  [Page.Order]: {
    orderSource: OrderSource;
    siteId?: string | null;
    tableNumber?: string | null;
    menuCode?: string | null;
    orderTypeCode?: string | null;
  };
  [Page.Checkout]: {
    orderSource: OrderSource;
    siteId?: string | null;
    tableNumber?: string | null;
    menuCode?: string | null;
  };
};

export const urlGen = <T extends keyof typeof Page>(
  page: T,
  params: PageParams[(typeof Page)[T]],
): string => {
  if (page === "Sites") {
    return "/sites";
  }
  if (page === "Orders") {
    return "/orders";
  }
  if (page === "Order") {
    const { orderSource, siteId, tableNumber, menuCode, orderTypeCode } =
      params as PageParams[Page.Order];

    const searchParams = new URLSearchParams();

    if (menuCode) {
      searchParams.append("menuCode", menuCode);
    }
    if (orderTypeCode) {
      searchParams.append("orderTypeCode", orderTypeCode);
    }

    const pathname = compact([
      formatUrlSegment(orderSource),
      siteId || null,
      tableNumber,
    ]).join("/");

    return createPath({
      pathname: `/${pathname}`,
      search: searchParams.toString() || undefined,
    });
  }
  if (page === "Checkout") {
    const { orderSource, siteId, tableNumber, menuCode } =
      params as PageParams[Page.Checkout];

    const searchParams = new URLSearchParams();

    if (menuCode) {
      searchParams.append("menuCode", menuCode);
    }

    const pathname = compact([
      formatUrlSegment(orderSource),
      siteId || null,
      tableNumber,
      "checkout",
    ]).join("/");

    return createPath({
      pathname: `/${pathname}`,
      search: searchParams.toString() || undefined,
    });
  }

  if (page === "Address") {
    return "/address";
  }

  return "/";
};

export const routeIsActive = (routeUrl: string, currentUrl: string) => {
  let splitRouteUrl = routeUrl.split("/");
  let splitCurrentUrl = currentUrl.split("/");

  if (routeUrl === "/") {
    // Routes with url "/" should be considered activated if and only if the route matches exactly
    // HOWEVER we can be generous about whether the route ends in a / or not

    splitCurrentUrl = splitCurrentUrl.filter(
      (x, i) => !(x === "" && i === splitCurrentUrl.length - 1),
    );
    splitRouteUrl = splitRouteUrl.filter(
      (x, i) => !(x === "" && i === splitRouteUrl.length - 1),
    );

    if (splitCurrentUrl.length !== splitRouteUrl.length) {
      return false;
    }
  } else {
    if (splitCurrentUrl.length < splitRouteUrl.length) {
      return false;
    }
  }

  for (let i = 0; i < splitRouteUrl.length; i++) {
    if (
      !splitRouteUrl[i].startsWith(":") &&
      splitRouteUrl[i] !== splitCurrentUrl[i]
    ) {
      return false;
    }
  }
  return true;
};

export const routeIsActiveStrict = (routeUrl: string, currentUrl: string) => {
  let splitRouteUrl = routeUrl.split("/");
  let splitCurrentUrl = currentUrl.split("/");

  if (splitRouteUrl.length !== splitCurrentUrl.length) {
    return false;
  }

  if (routeUrl === "/") {
    // Routes with url "/" should be considered activated if and only if the route matches exactly
    // HOWEVER we can be generous about whether the route ends in a / or not

    splitCurrentUrl = splitCurrentUrl.filter(
      (x, i) => !(x === "" && i === splitCurrentUrl.length - 1),
    );
    splitRouteUrl = splitRouteUrl.filter(
      (x, i) => !(x === "" && i === splitRouteUrl.length - 1),
    );

    if (splitCurrentUrl.length !== splitRouteUrl.length) {
      return false;
    }
  } else {
    if (splitCurrentUrl.length < splitRouteUrl.length) {
      return false;
    }
  }

  for (let i = 0; i < splitRouteUrl.length; i++) {
    if (
      !splitRouteUrl[i].startsWith(":") &&
      splitRouteUrl[i] !== splitCurrentUrl[i]
    ) {
      return false;
    }
  }
  return true;
};

export const isPaymentPage = (pathname: string) =>
  pathname.endsWith("payment") ||
  pathname.endsWith("declined") ||
  pathname.endsWith("success") ||
  pathname.endsWith("cancelled");

export const getRouteEnd = (pathname: string): string => {
  const split = pathname.split("?")[0].split("/");
  return split[split.length - 1];
};
